import { useMutation } from "@apollo/client";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertPresence } from "utils/assertions";
import { DocumentType, gql } from "__generated__/gql";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { getUpdateOrCreateVariables } from "components/Shared/Forms/AddressFieldset";
import { getUpdateOrCreatePhoneNumberVariables } from "components/Shared/Forms/PhoneInput";
import { newDistributionCenterCacheUpdate } from "./utils";
import { FormFields, processApolloError } from "./Form";

const createMutation = gql(/* GraphQL */ `
  mutation CreateDistributionCenter(
    $data: DistributionCenterInput!
    $isAddressUpdatedOrCreated: Boolean = false
    $addressId: UUID
    $addressData: AddressPatch
    $isPhoneNumberUpdatedOrCreated: Boolean = false
    $phoneNumberId: UUID
    $phoneNumberData: PhoneNumberPatch
  ) {
    ...UpdateOrCreateAddress
    ...UpdateOrCreatePhoneNumber
    createDistributionCenter(input: { distributionCenter: $data }) {
      distributionCenter {
        id
        orderingPlatformId
        ...DistributionCentersDirectoryTableRow
        ...DistributionCenterDetails
        ...CreateDistributionCenterCacheUpdate
      }
    }
  }
`);

export type MutationResult = DocumentType<typeof createMutation>;

type Props = {
  isAdmin?: boolean;
  metadata?: GraphQLJSON;
  onCompleted?: (data: MutationResult) => void;
};

export default function useCreateDistributionCenter({
  isAdmin,
  metadata,
  onCompleted,
}: Props) {
  const brandContext = useActiveBrandIdContext();

  const [performCreate, { loading: isUpdating }] = useMutation(createMutation, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    update: (cache, result) => {
      const newDc = result.data?.createDistributionCenter?.distributionCenter;
      assertPresence(newDc);
      newDistributionCenterCacheUpdate(cache, newDc);
    },
    onCompleted,
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: FormFields) => {
      const { address, phoneNumber, ...locationData } = formData;

      const addressVariables = getUpdateOrCreateVariables(undefined, address);

      const phoneNumberVariables = getUpdateOrCreatePhoneNumberVariables(
        undefined,
        phoneNumber,
      );

      return performCreate({
        variables: {
          data: {
            ...locationData,
            addressId: addressVariables.addressId,
            phoneNumberId: phoneNumberVariables.phoneNumberId,
            metadata,
          },
          ...addressVariables,
          ...phoneNumberVariables,
        },
      });
    },
    {
      processApolloError,
      successMessage: "Distribution center created successfully",
    },
  );

  return {
    handleValidSubmit,
    isUpdating,
  };
}
